import React, { useEffect, useState } from 'react';
import { 
  FaRocket, 
  FaLightbulb, 
  FaUsers, 
  FaTrophy, 
  FaClock, 
  FaHeart,
  FaShieldAlt 
} from 'react-icons/fa';

const WhyUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Modal popup for CTA form
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [journeyResult, setJourneyResult] = useState('');

  useEffect(() => {
    console.log('Why Us section viewed');
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  const handleJourneySubmit = async (event) => {
    event.preventDefault();
    setJourneyResult('Sending...');
    const formData = new FormData(event.target);
    formData.append('access_key', '4b701ea6-eb3e-4c6a-a8d8-c13c4196b5de');
    formData.append('subject', 'Start Your Journey Today');

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData
    });

    const data = await response.json();
    if (data.success) {
      setJourneyResult('Form Submitted Successfully');
      event.target.reset();
    } else {
      setJourneyResult('Error sending form. Please try again.');
    }
  };

  const reasons = [
    {
      icon: <FaRocket className="text-4xl" />,
      title: "Lightning Fast Results",
      description: "We don't just promise speed - we deliver results that make your competitors wonder what hit them.",
      color: "from-blue-500 to-purple-600",
      delay: "0ms"
    },
    {
      icon: <FaLightbulb className="text-4xl" />,
      title: "Creative Innovation",
      description: "Our ideas are so fresh, they come with their own expiration date. We think outside the box, then redesign the box.",
      color: "from-yellow-400 to-orange-500",
      delay: "200ms"
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: "Expert Team",
      description: "Our team doesn't just know trends - we create them. Each member is a specialist who eats, sleeps, and dreams digital success.",
      color: "from-green-400 to-teal-500",
      delay: "400ms"
    },
    {
      icon: <FaTrophy className="text-4xl" />,
      title: "Proven Track Record",
      description: "We've helped 500+ brands transform from 'who?' to 'wow!' with measurable results that speak louder than words.",
      color: "from-purple-500 to-pink-500",
      delay: "600ms"
    },
    {
      icon: <FaClock className="text-4xl" />,
      title: "24/7 Support",
      description: "Questions at 3 AM? We've got you covered. Our support team is more reliable than your morning coffee.",
      color: "from-red-400 to-pink-500",
      delay: "800ms"
    },
    {
      icon: <FaHeart className="text-4xl" />,
      title: "Passion-Driven",
      description: "We don't just work for you - we obsess over your success. Your wins are our wins, and we celebrate accordingly.",
      color: "from-pink-400 to-red-500",
      delay: "1000ms"
    }
  ];

  const stats = [
    { number: "500+", label: "Happy Clients", icon: <FaUsers className="text-2xl" /> },
    { number: "1000+", label: "Projects Completed", icon: <FaTrophy className="text-2xl" /> },
    { number: "99%", label: "Client Satisfaction", icon: <FaHeart className="text-2xl" /> },
    { number: "24/7", label: "Support Available", icon: <FaShieldAlt className="text-2xl" /> }
  ];

  return (
    <section id="whyus" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400/20 to-yellow-400/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-indigo-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header Section */}
        <div className={`text-center mb-16 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-semibold mb-6 shadow-lg">
            ✨ Why Choose Us?
          </div>
          <h2 className="text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Why Choose 
            </span>
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We don't just combine strategy, creativity, and technology - we blend them into a powerful cocktail that delivers 
            <span className="font-semibold text-blue-600"> impactful solutions</span> and drives 
            <span className="font-semibold text-purple-600"> extraordinary business results</span>.
          </p>
        </div>

        {/* Stats Section */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-20 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{transitionDelay: '300ms'}}>
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20 hover:scale-105 group"
              style={{animationDelay: `${index * 100}ms`}}
            >
              <div className="text-blue-600 mb-3 group-hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {stat.number}
              </div>
              <div className="text-sm text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => (
            <div 
              key={index}
              className={`group relative bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/30 hover:scale-105 hover:-translate-y-2 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{
                transitionDelay: `${600 + index * 100}ms`,
                animationDelay: reason.delay
              }}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${reason.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-3xl`}></div>
              {/* Icon */}
              <div className={`inline-flex p-4 bg-gradient-to-br ${reason.color} text-white rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                {reason.icon}
              </div>
              {/* Content */}
              <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                {reason.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {reason.description}
              </p>
              {/* Hover Effect Line */}
              <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${reason.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-3xl`}></div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`text-center transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{transitionDelay: '1200ms'}}>
          <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-white/30 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Ready to Experience the WETRND Difference?
            </h3>
            <p className="text-gray-600 mb-6">
              Join hundreds of satisfied clients who've transformed their digital presence with us.
            </p>
            <button
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              onClick={() => { setShowJourneyModal(true); setJourneyResult(''); }}
            >
              <span className="relative z-10">Start Your Journey Today</span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>
          </div>
        </div>

        {showJourneyModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-lg relative">
              <button
                className="absolute top-3 right-3 text-xl text-gray-500"
                onClick={() => setShowJourneyModal(false)}
              >
                &times;
              </button>
              {journeyResult === "Form Submitted Successfully" ? (
                <div className="text-center">
                  <h2 className="font-bold text-xl mb-4 text-green-600">Thank you!</h2>
                  <p>Your submission has been sent. Our team will contact you soon.</p>
                  <button
                    className="mt-4 text-blue-500 underline"
                    onClick={() => setShowJourneyModal(false)}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleJourneySubmit} className="flex flex-col gap-4">
                  <h2 className="font-bold text-2xl mb-2">Start Your Journey today</h2>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                    className="border rounded p-2"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    required
                    className="border rounded p-2"
                  />
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    required
                    className="border rounded p-2"
                  />
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
                    Submit
                  </button>
                  <span className="text-center text-sm text-gray-500">{journeyResult}</span>
                </form>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-4 h-4 bg-blue-400 rounded-full opacity-60 animate-bounce" style={{animationDelay: '0s'}}></div>
      <div className="absolute top-32 right-20 w-3 h-3 bg-purple-400 rounded-full opacity-60 animate-bounce" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-20 left-20 w-5 h-5 bg-pink-400 rounded-full opacity-60 animate-bounce" style={{animationDelay: '2s'}}></div>
      <div className="absolute bottom-32 right-10 w-3 h-3 bg-yellow-400 rounded-full opacity-60 animate-bounce" style={{animationDelay: '1.5s'}}></div>
    </section>
  );
};

export default WhyUs;
